import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { fetchMyMessages, sendMyMessage, userReactToMessage, studioAuth, pingUserTyping, fetchUserTypingStatus, fetchStudioPeer } from "../lib/studioApi";
import { Button } from "../components/ui/button";
import { Send, Loader2, Paperclip, X, Music, Video, Image as ImageIcon, FileText } from "lucide-react";
import { toast } from "sonner";
import VoiceRecorder from "../components/studio/VoiceRecorder";
import DmBubble from "../components/studio/DmBubble";
import TypingDots from "../components/studio/TypingDots";
import Avatar from "../components/studio/Avatar";
import EmojiButton from "../components/studio/EmojiButton";
import useDmAttachments from "../hooks/useDmAttachments";
import useTypingPing from "../hooks/useTypingPing";
import useNewMessageNotification from "../hooks/useNewMessageNotification";

function KindIcon({ kind }) {
  if (kind === "audio") return <Music className="w-4 h-4" />;
  if (kind === "video") return <Video className="w-4 h-4" />;
  if (kind === "image") return <ImageIcon className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
}

export default function StudioMessages() {
  useOutletContext();
  const me = studioAuth.getUser() || {};
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [peer, setPeer] = useState({ name: "Paul", artistName: "Paul Vilas", avatarUrl: "", lastSeenAt: "" });
  const [voicePhase, setVoicePhase] = useState("idle");
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const attachments = useDmAttachments({ asAdmin: false });
  const [adminTyping, setAdminTyping] = useState(false);
  const pingTyping = useTypingPing(pingUserTyping);

  // Pop sound when Paul (admin) sends a new message into this thread.
  useNewMessageNotification(msgs, (m) => m && m.senderRole === "admin");

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) {
      setText((t) => t + emoji);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    // Restore cursor right after the inserted emoji.
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  };

  useEffect(() => {
    const tick = () => {
      fetchMyMessages()
        .then((data) => {
          setMsgs(data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
      fetchUserTypingStatus().then(setAdminTyping).catch(() => {});
      fetchStudioPeer().then(setPeer).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const send = async () => {
    const body = text.trim();
    const ready = attachments.pending.length === 0 || attachments.allDone;
    if (!ready) {
      toast.error("Wait for uploads to finish");
      return;
    }
    const atts = attachments.finalize();
    if (!body && atts.length === 0) return;
    if (sending) return;
    setSending(true);
    try {
      const m = await sendMyMessage(body, atts);
      setMsgs((prev) => [...prev, m]);
      setText("");
      attachments.clear();
    } catch {
      toast.error("Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (attachment) => {
    try {
      const m = await sendMyMessage("", [attachment]);
      setMsgs((prev) => [...prev, m]);
    } catch {
      toast.error("Failed to send voice message");
    }
  };

  const onReact = async (messageId, emoji) => {
    try {
      const updated = await userReactToMessage(messageId, emoji);
      setMsgs((prev) => prev.map((m) => (m.id === messageId ? updated : m)));
    } catch {
      toast.error("Reaction failed");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const hasContent = text.trim() || attachments.pending.length > 0;

  return (
    <div
      data-testid="studio-messages"
      className="bg-[#10162a] border border-white/5 rounded-2xl flex flex-col h-[calc(100vh-180px)] min-h-[480px] overflow-hidden"
    >
      <header className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
        <Avatar user={peer} size="md" showDot ringColor="border-[#10162a]" testIdPrefix="studio-peer-avatar" />
        <div className="flex-1">
          <div className="font-semibold text-white">{peer.artistName || "Paul Vilas"}</div>
          {adminTyping ? (
            <div className="text-xs text-paul-yellow flex items-center gap-1.5" data-testid="studio-typing-indicator">
              <TypingDots theme="dark" />
              <span>Paul is typing…</span>
            </div>
          ) : (
            <div className="text-xs text-white/40">Direct message · usually replies within a day</div>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-3">
        {loading ? (
          <div className="text-center text-white/40 py-10">
            <Loader2 className="w-5 h-5 animate-spin inline-block" />
          </div>
        ) : msgs.length === 0 ? (
          <div className="text-center text-white/40 py-16 text-sm">
            No messages yet. Say hi to Paul 👋
          </div>
        ) : (
          msgs.map((m, i) => {
            const isLastMine =
              m.senderRole === "user" &&
              !msgs.slice(i + 1).some((x) => x.senderRole === "user");
            return (
              <DmBubble
                key={m.id}
                msg={m}
                mine={m.senderRole === "user"}
                myId={me.id}
                isLast={isLastMine}
                onReact={onReact}
              />
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 md:p-4 border-t border-white/5 bg-[#0b0f1e]">
        {attachments.pending.length > 0 && (
          <div className="space-y-2 mb-2" data-testid="studio-pending-attachments">
            {attachments.pending.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 bg-[#161a2c] rounded-lg p-2 border border-white/5"
              >
                <div className="w-7 h-7 rounded-md bg-paul-yellow/15 text-paul-yellow flex items-center justify-center flex-shrink-0">
                  <KindIcon kind={p.kind} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white truncate">{p.file.name}</div>
                  <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
                    <div className="h-full bg-paul-yellow transition-all" style={{ width: `${p.progress}%` }} />
                  </div>
                </div>
                <span className="text-[10px] text-white/40 font-mono min-w-[32px] text-right">{p.progress}%</span>
                <button type="button" onClick={() => attachments.removePending(p.id)} className="text-white/40 hover:text-red-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          {voicePhase === "idle" && (
            <>
              <button
                type="button"
                data-testid="studio-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                className="h-11 w-11 rounded-xl border border-white/10 text-white/70 hover:text-paul-yellow hover:border-paul-yellow/60 flex-shrink-0 flex items-center justify-center transition-colors"
                title="Attach files"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) attachments.addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </>
          )}
          <VoiceRecorder asAdmin={false} onSend={sendVoice} theme="dark" onPhaseChange={setVoicePhase} />
          {voicePhase === "idle" && (
            <>
              <EmojiButton
                theme="dark"
                testId="studio-emoji-btn"
                onPick={insertEmoji}
              />
              <textarea
                ref={textareaRef}
                data-testid="studio-message-input"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (e.target.value.trim()) pingTyping();
                }}
                onKeyDown={onKey}
                placeholder="Message Paul…"
                rows={1}
                className="flex-1 resize-none bg-[#10162a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-paul-yellow/60 max-h-32"
                style={{ minHeight: 44 }}
              />
              <Button
                data-testid="studio-message-send"
                onClick={send}
                disabled={sending || !hasContent}
                className="h-11 px-4 bg-paul-yellow hover:bg-paul-yellow text-navy font-bold gap-2"
                style={{ color: "#0b1535" }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                <span className="hidden sm:inline">Send</span>
              </Button>
            </>
          )}
        </div>
        <p className="text-[11px] text-white/30 mt-2 hidden md:block">
          📎 Attach · 🎙 Voice · 😊 Emoji · Shift + Enter for newline
        </p>
      </div>
    </div>
  );
}
